// Shared IST-aware date/time formatters for all dSpr surfaces.
//
// Single source of truth. Every dSpr page imports from here so we don't
// drift the locale, the time zone, or the AM/PM convention across files.
//
// Locale is "en-IN" → "10 Jun 2026, 12:42 PM" (day-first, matches the
// audience). Time zone is pinned to "Asia/Kolkata" so the browser's TZ
// can't bleed through.

const IST_TZ    = "Asia/Kolkata";
const LOCALE_IN = "en-IN";
const LOCALE_CA = "en-CA";   // en-CA yields YYYY-MM-DD natively (no zero-pad math)

// ── Time-aware (DATETIME / TIMESTAMP) ───────────────────────────────────────

// "10 Jun 2026, 12:42 PM" - primary formatter for sent/submitted/last-event.
// Accepts ISO string, JS Date, or null. `withSuffix:true` appends " IST"
// (used by PDF + Excel exports where the timezone needs to be explicit).
export function fmtDateTimeIST(val, { withSuffix = false } = {}) {
    if (!val) return "-";
    const d = val instanceof Date ? val : new Date(val);
    if (isNaN(d.getTime())) return String(val);
    const base = d.toLocaleString(LOCALE_IN, {
        timeZone: IST_TZ,
        day: "2-digit", month: "short", year: "numeric",
        hour: "numeric", minute: "2-digit", hour12: true,
    });
    return withSuffix ? `${base} IST` : base;
}

// Compact variant - drops the year. Used in narrow inline pills where
// the cycle context already implies the year (e.g. "Jun 9, 12:47 PM").
export function fmtDateTimeShort(val) {
    if (!val) return "-";
    const d = val instanceof Date ? val : new Date(val);
    if (isNaN(d.getTime())) return String(val);
    return d.toLocaleString(LOCALE_IN, {
        timeZone: IST_TZ,
        day: "2-digit", month: "short",
        hour: "numeric", minute: "2-digit", hour12: true,
    });
}

// ── Date-only (DATE - no time component) ────────────────────────────────────
//
// DB returns DATE columns as raw "YYYY-MM-DD" strings (thanks to
// dateStrings:["DATE"] in config/db.js). The `T00:00:00` trick forces
// JS to interpret the string as LOCAL midnight, not UTC midnight - so
// "2026-06-30" renders as "30 Jun 2026" in IST instead of "29 Jun 2026".

export function fmtDate(val) {
    if (!val) return "-";
    const iso10 = typeof val === "string" && val.length === 10 ? `${val}T00:00:00` : val;
    const d = val instanceof Date ? val : new Date(iso10);
    if (isNaN(d.getTime())) return String(val);
    return d.toLocaleDateString(LOCALE_IN, {
        timeZone: IST_TZ,
        day: "2-digit", month: "short", year: "numeric",
    });
}

// "10 June 2026" - full month name. Used in human-readable copy
// (confirmation dialog body, cycle window summary, etc.).
export function fmtDateLong(val) {
    if (!val) return "-";
    const iso10 = typeof val === "string" && val.length === 10 ? `${val}T00:00:00` : val;
    const d = val instanceof Date ? val : new Date(iso10);
    if (isNaN(d.getTime())) return String(val);
    return d.toLocaleDateString(LOCALE_IN, {
        timeZone: IST_TZ,
        day: "2-digit", month: "long", year: "numeric",
    });
}

// "10 Jun - 30 Jun 2026" (same year) or "10 Jun 2026 - 5 Jan 2027".
// Used by the review form to render the cycle reporting period.
export function fmtDateRange(start, end) {
    if (!start || !end) return "-";
    const norm = (v) => typeof v === "string" && v.length === 10 ? `${v}T00:00:00` : v;
    const s = new Date(norm(start));
    const e = new Date(norm(end));
    if (isNaN(s.getTime()) || isNaN(e.getTime())) return `${start} - ${end}`;
    const sameYear = s.getFullYear() === e.getFullYear();
    const opts = sameYear
        ? { timeZone: IST_TZ, day: "2-digit", month: "short" }
        : { timeZone: IST_TZ, day: "2-digit", month: "short", year: "numeric" };
    const endOpts = { timeZone: IST_TZ, day: "2-digit", month: "short", year: "numeric" };
    return `${s.toLocaleDateString(LOCALE_IN, opts)} - ${e.toLocaleDateString(LOCALE_IN, endOpts)}`;
}

// ── Date filter helpers ─────────────────────────────────────────────────────
//
// IST today as "YYYY-MM-DD" - for FROM/TO defaults so "30 days ago" really
// means "30 IST days ago", not "30 browser-TZ days ago". en-CA emits
// YYYY-MM-DD natively which sidesteps the usual zero-pad / locale gymnastics.

export function istTodayISODate(daysAgo = 0) {
    const fmt = new Intl.DateTimeFormat(LOCALE_CA, { timeZone: IST_TZ });
    const d = new Date();
    d.setDate(d.getDate() - daysAgo);
    return fmt.format(d);
}

// "12 Jun 2026" - used in PDF / Excel "Generated" headers. Always today.
export function fmtGenDate() {
    return new Date().toLocaleDateString(LOCALE_IN, {
        timeZone: IST_TZ,
        day: "2-digit", month: "short", year: "numeric",
    });
}
